import { authLogout } from 'actions/AuthActions'
import AvatarCircle from 'components/Common/AvatarCircle/AvatarCircle'
import TextButton from 'LuxKit/Button/TextButton'
import DropdownList from 'LuxKit/Dropdown/List/DropdownList'
import ClickableListItem from 'LuxKit/List/ClickableListItem'
import Caption from 'LuxKit/Typography/Caption'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import config from 'constants/config'
import ModalContext from 'contexts/ModalContext'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import useToggle from 'hooks/useToggle'
import { useScreenSizeOnly } from 'hooks/useScreenSize'
import { useCallback, useContext, useRef } from 'react'
import { useIntl } from 'react-intl'
import AgentHubAccountMenuModal from './AgentHubAccountMenuModal'
import { pushWithRegion } from 'actions/NavigationActions'

function AgentHubAccountMenu() {
  const intl = useIntl()
  const [menuOpen, toggleMenuOpen, _, closeMenu] = useToggle(false)

  const dropdownTrigger = useRef<HTMLDivElement>(null)
  const firstName = useAppSelector(state => state.auth.account.givenName)
  const lastName = useAppSelector(state => state.auth.account.surname)
  const dispatch = useAppDispatch()

  const isMobile = useScreenSizeOnly('mobile')

  const showModal = useContext(ModalContext)
  const onLogout = useCallback(() => {
    dispatch(authLogout())
    dispatch(pushWithRegion('/'))
  }, [dispatch])

  const onMenuClick = useCallback(() => {
    if (isMobile) {
      showModal(<AgentHubAccountMenuModal />)
    } else {
      toggleMenuOpen()
    }
  }, [isMobile, showModal, toggleMenuOpen])

  const accountAriaLabel = intl.formatMessage({
    id: 'gorJlYuG-iz5dBWrK-bw',
    defaultMessage: 'Account',
    description: 'Aria-label for the account menu button in agent hub header',
  })

  const accountCaption = intl.formatMessage({
    id: 'QK9DnlFk5MzJzC6R8lGi',
    defaultMessage: 'Account',
    description: 'Caption text on the account menu button in agent hub header',
  })

  const viewDashboardTitle = intl.formatMessage({
    id: '1F5_SFM0AvByo4kJQ5iL',
    defaultMessage: 'View dashboard',
    description: 'List item to navigate to agent portal dashboard in agent hub account dropdown',
  })

  const logOutTitle = intl.formatMessage({
    id: 'vt47BM8mzbA_XPoJV7z8',
    defaultMessage: 'Log out',
    description: 'List item to log out in agent hub account dropdown',
  })

  return <>
    <div ref={dropdownTrigger}>
      <CSSBreakpoint min="desktop">
        <TextButton
          kind="tertiary"
          variant="dark"
          aria-label={accountAriaLabel}
          onClick={onMenuClick}
          startIcon={<AvatarCircle
            size="small"
            firstName={firstName}
            lastName={lastName}
            nonInteractable
          />}
        >
          <Caption variant="medium" weight="bold">
            {accountCaption}
          </Caption>
        </TextButton>
      </CSSBreakpoint>
      <CSSBreakpoint max="tablet">
        <AvatarCircle
          size="small"
          firstName={firstName}
          lastName={lastName}
          onClick={onMenuClick}
        />
      </CSSBreakpoint>
    </div>
    <DropdownList
      size="S"
      open={menuOpen}
      triggerRef={dropdownTrigger}
      anchorRef={dropdownTrigger}
      onClose={closeMenu}
      placement="bottom-end"
    >
      <ClickableListItem title={viewDashboardTitle} href={config.AGENT_PORTAL_URL} target="_blank" />
      <ClickableListItem title={logOutTitle} onClick={onLogout} />
    </DropdownList>
  </>
}

export default AgentHubAccountMenu
